/**
 * Owns every piece of state + side-effect for the Statement page:
 *   - period selection (year / month) bounded by the customer's tier
 *   - the actual GET /api/accounts/:id/statement fetch
 *   - account-types lookup (needed to render the right icon)
 *   - tier-aware navigation guards (next-arrow lock + toast)
 *
 * Returns a single flat object the shell + views destructure. No JSX in
 * here — pure React state plumbing.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { maxForecastMonths, TIER_LABEL, effectiveTier } from '../../lib/tier';
import { MONTHS } from './constants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useStatement() {
  const { accountId } = useParams();
  const { user } = useAuth();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [statement, setStatement] = useState(null);
  const [accountTypes, setAccountTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Tier-based forecast cap. Statement navigation forward in time honours the
  // same horizon as the Dashboard chart: Ember 6 mo, Steel 18 mo, Damascus 36
  // mo (admin/staff get Damascus). Otherwise paying users would have no perk
  // over free users since they could just paginate one month at a time.
  const tierHorizonMonths = maxForecastMonths(user);
  const tierLabel = TIER_LABEL[effectiveTier(user)] || 'Ember';

  // Latest year-month a customer is allowed to view.
  const maxAllowed = useMemo(() => {
    const now = new Date();
    const target = new Date(now.getFullYear(), now.getMonth() + tierHorizonMonths, 1);
    return { year: target.getFullYear(), month: target.getMonth() + 1 };
  }, [tierHorizonMonths]);

  const isAfterCap = useCallback((y, m) => {
    if (y > maxAllowed.year) return true;
    if (y < maxAllowed.year) return false;
    return m > maxAllowed.month;
  }, [maxAllowed]);

  const atCap = isAfterCap(year, month) || (year === maxAllowed.year && month === maxAllowed.month);

  // If the chosen month/year drift past the tier cap (e.g. user picks year
  // then month independently), bounce them back and explain why.
  useEffect(() => {
    if (isAfterCap(year, month)) {
      setMonth(maxAllowed.month);
      setYear(maxAllowed.year);
      toast.info(`Forge Ledger ${tierLabel} can forecast up to ${tierHorizonMonths} months ahead. Upgrade to see further.`, {
        id: 'statement-horizon-cap',
      });
    }
  }, [year, month, isAfterCap, maxAllowed.month, maxAllowed.year, tierLabel, tierHorizonMonths]);

  const fetchStatement = useCallback(async () => {
    setLoading(true);
    try {
      const [stmtRes, typesRes] = await Promise.all([
        axios.get(`${API}/accounts/${accountId}/statement?year=${year}&month=${month}`, { withCredentials: true }),
        axios.get(`${API}/account-types`, { withCredentials: true }),
      ]);
      setStatement(stmtRes.data);
      setAccountTypes(typesRes.data);
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('Account not found');
      } else {
        toast.error('Failed to load statement');
      }
      setStatement(null);
    } finally {
      setLoading(false);
    }
  }, [accountId, year, month]);

  useEffect(() => {
    fetchStatement();
  }, [fetchStatement]);

  const stepMonth = (delta) => {
    let newMonth = month + delta;
    let newYear = year;
    if (newMonth < 1) { newMonth = 12; newYear -= 1; }
    if (newMonth > 12) { newMonth = 1; newYear += 1; }
    if (delta > 0 && isAfterCap(newYear, newMonth)) {
      toast.info(`Forge Ledger ${tierLabel} can forecast up to ${tierHorizonMonths} months ahead. Upgrade to see further.`, {
        id: 'statement-horizon-cap',
      });
      return;
    }
    setMonth(newMonth);
    setYear(newYear);
  };

  // Build year list dynamically — earliest is today minus 3 years, latest
  // is the year containing the tier-allowed forecast cap (whichever is
  // later).
  const yearOptions = useMemo(() => {
    const startYear = new Date().getFullYear() - 3;
    const endYear = Math.max(maxAllowed.year, startYear + 1);
    const out = [];
    for (let y = startYear; y <= endYear; y += 1) out.push(y);
    return out;
  }, [maxAllowed.year]);

  // For the month dropdown — when viewing the cap year, hide months above
  // the cap month so customers can't sidestep the next-arrow guard.
  const monthOptions = useMemo(() => {
    if (year < maxAllowed.year) return MONTHS;
    return MONTHS.filter((m) => m.value <= maxAllowed.month);
  }, [year, maxAllowed.year, maxAllowed.month]);

  return {
    // flags
    loading,
    atCap,
    tierHorizonMonths,
    tierLabel,
    // state
    year,
    month,
    statement,
    accountTypes,
    yearOptions,
    monthOptions,
    // setters
    setYear,
    setMonth,
    // handlers
    stepMonth,
  };
}
